import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Box,
} from '@mui/material';
import { Person } from '@mui/icons-material';
import { IFuncionario } from '../../types';
import funcionarioService from '../../services/funcionarioService';

interface FuncionariosSelectProps {
  value?: number | null;
  onChange: (value: number | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  allowClear?: boolean;
}

const FuncionariosSelect: React.FC<FuncionariosSelectProps> = ({
  value = null,
  onChange,
  label = 'Vendedor',
  placeholder = 'Selecione o vendedor',
  required = false,
  disabled = false,
  error = false,
  helperText,
  size = 'medium',
  fullWidth = true,
  allowClear = true,
}) => {
  const [funcionarios, setFuncionarios] = useState<IFuncionario[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFuncionarios();
  }, []);

  const loadFuncionarios = async () => {
    try {
      setLoading(true);
      const response = await funcionarioService.getFuncionariosAtivos();
      const data = (response as any)?.data ?? response;
      setFuncionarios(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao carregar funcionários:', err);
      setFuncionarios([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: any) => {
    const v = event.target.value;
    onChange(v === '' ? null : Number(v));
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      error={error}
      disabled={disabled}
      required={required}
      variant="outlined"
    >
      <InputLabel>{label}</InputLabel>
      <Select
        size="small"
        value={value ?? ''}
        onChange={handleChange}
        label={label}
        renderValue={(selected: number | string) => {
          if (selected === '' || selected == null) {
            return <Box component="span" sx={{ color: 'text.disabled' }}>{placeholder}</Box>;
          }
          const f = funcionarios.find((x) => x.id === selected);
          return f ? `${f.nome}${f.cargo ? ` - ${f.cargo}` : ''}` : String(selected);
        }}
      >
        {allowClear && (
          <MenuItem value="">
            <em>Não informado</em>
          </MenuItem>
        )}
        {/* Evita o warning do MUI de valor fora do range durante o loading */}
        {value && !funcionarios.some(f => f.id === value) && (
          <MenuItem value={value} sx={{ display: 'none' }}>
            {value}
          </MenuItem>
        )}
        {loading ? (
          <MenuItem value="" disabled>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              Carregando...
            </Box>
          </MenuItem>
        ) : (
          funcionarios.map((f) => (
            <MenuItem key={f.id} value={f.id}>
              <Box display="flex" alignItems="center" gap={1}>
                <Person fontSize="small" color="action" />
                {f.nome}
                {f.cargo && (
                  <Box component="span" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                    - {f.cargo}
                  </Box>
                )}
              </Box>
            </MenuItem>
          ))
        )}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default FuncionariosSelect;
